import { Link } from "react-router-dom";
import { Heart, Laptop, ShoppingCart, Star } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { formatPrice } from "@/lib/format";
import { stripRichText } from "@/components/RichText";

const PLACEHOLDER_IMG = "data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20width='400'%20height='400'%3E%3Crect%20width='400'%20height='400'%20fill='%23EEF2F7'/%3E%3Ctext%20x='200'%20y='210'%20font-family='Arial'%20font-size='24'%20font-weight='bold'%20fill='%2394A3B8'%20text-anchor='middle'%3EISMO-TECH%3C/text%3E%3C/svg%3E";


export function ProductCard({ product, index = 0 }) {
  const { addToCart, toggleFavorite, isFavorite, currency } = useStore();
  const favorite = isFavorite(product.id);
  const outOfStock = !product.in_stock;

  return (
    <article
      data-testid={`product-card-${product.slug}`}
      style={{ animationDelay: `${Math.min(index, 8) * 45}ms` }}
      className="group flex animate-fade-up flex-col overflow-hidden rounded-2xl border border-line bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(0,0,0,0.09)]"
    >
      <div className="relative">
        <Link
          to={`/boutique/${product.slug}`}
          data-testid={`product-link-${product.slug}`}
          className="block bg-slate-50/80 p-3 sm:p-5"
        >
          <img
            src={product.image_url || PLACEHOLDER_IMG}
            alt={product.name}
            loading={index > 3 ? "lazy" : "eager"}
            decoding="async"
            className="mx-auto aspect-square w-full max-w-[190px] object-contain transition-transform duration-500 group-hover:scale-[1.06]"
          />
        </Link>

        {product.discount_percent > 0 && (
          <span
            data-testid={`product-discount-${product.slug}`}
            className="absolute left-2.5 top-2.5 rounded-full bg-rose-500 px-2 py-1 text-[10px] font-black text-white sm:px-2.5 sm:text-[11px]"
          >
            -{product.discount_percent}%
          </span>
        )}

        {outOfStock && (
          <span className="absolute left-2.5 top-9 rounded-full bg-ink px-2 py-1 text-[10px] font-bold text-white">
            Rupture
          </span>
        )}

        <button
          data-testid={`product-favorite-${product.slug}`}
          aria-label="Ajouter aux favoris"
          onClick={() => toggleFavorite(product)}
          className={`absolute right-2.5 top-2.5 flex h-8 w-8 items-center justify-center rounded-full border transition-colors duration-200 ${
            favorite
              ? "border-rose-200 bg-rose-50 text-rose-500"
              : "border-line bg-white text-slate-400 hover:border-rose-200 hover:text-rose-500"
          }`}
        >
          <Heart className="h-4 w-4" fill={favorite ? "currentColor" : "none"} />
        </button>
      </div>

      <div className="flex flex-1 flex-col p-3 sm:p-4">
        <p className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-brand sm:text-[10px]">
          {product.license_type}
        </p>
        <Link to={`/boutique/${product.slug}`} className="mt-1.5 block">
          <h3 className="line-clamp-2 text-[13.5px] font-bold leading-snug text-ink transition-colors duration-200 hover:text-brand sm:text-[15px]">
            {product.name}
          </h3>
        </Link>

        {product.reviews_count > 0 && (
          <span className="mt-1.5 flex items-center gap-1" data-testid={`product-rating-${product.slug}`}>
            <Star className="h-3.5 w-3.5 text-amber-400" fill="currentColor" />
            <span className="text-xs font-bold text-ink">{product.rating}</span>
            <span className="text-[11px] text-slate-400">
              ({product.reviews_count} avis)
            </span>
          </span>
        )}

        <p className="mt-1.5 hidden text-xs leading-snug text-slate-500 sm:line-clamp-2">
          {stripRichText(product.short_description)}
        </p>

        {product.compatibility?.length > 0 && (
          <p className="mt-2 hidden items-center gap-1.5 text-[11px] font-medium text-slate-500 sm:flex">
            <Laptop className="h-3.5 w-3.5" />
            {product.compatibility.slice(0, 2).join(" · ")}
          </p>
        )}

        <div className="mt-auto pt-3">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span
              data-testid={`product-price-${product.slug}`}
              className="font-display text-base font-extrabold text-ink sm:text-lg"
            >
              {formatPrice(product.price, currency)}
            </span>
            {product.old_price > product.price && (
              <span className="text-xs text-slate-400 line-through">
                {formatPrice(product.old_price, currency)}
              </span>
            )}
          </div>

          <button
            data-testid={`product-add-to-cart-${product.slug}`}
            disabled={outOfStock}
            onClick={() => addToCart(product)}
            className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-full bg-brand px-3 py-2.5 text-[12.5px] font-bold text-white transition-[background-color,transform] duration-200 hover:bg-brand-dark active:scale-95 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500 sm:text-[13.5px]"
          >
            <ShoppingCart className="h-4 w-4" />
            {outOfStock ? "Indisponible" : "Ajouter au panier"}
          </button>
        </div>
      </div>
    </article>
  );
}

export function ProductCardSkeleton() {
  return (
    <div className="overflow-hidden rounded-2xl border border-line bg-white">
      <div className="aspect-square animate-pulse bg-slate-100" />
      <div className="space-y-2 p-4">
        <div className="h-2.5 w-16 animate-pulse rounded bg-slate-100" />
        <div className="h-4 w-full animate-pulse rounded bg-slate-100" />
        <div className="h-4 w-2/3 animate-pulse rounded bg-slate-100" />
        <div className="h-9 w-full animate-pulse rounded-full bg-slate-100" />
      </div>
    </div>
  );
}

export default ProductCard;
